import React from 'react';

/**
 * Summary-before-detail strip for Daily active users.
 * Renders two optional sections:
 *   • Engagement metrics — DAU / WAU / MAU + stickiness (range-anchored),
 *     shown when `metrics` is provided (US3 endpoint). The DAU card also
 *     shows the average DAU of the 7 days before the anchor (feature 138).
 *   • Device totals — Android / iOS / Android watch / iOS watch, each with
 *     total + avg/day, shown when a range `summary` is provided.
 *
 * Props: { metrics, summary, summaryDeltas, onViewWauUsers }
 *   onViewWauUsers — when given, the WAU card shows a "View users" button.
 */
const n = (v) => Number(v ?? 0);
const fmt = (v) => n(v).toLocaleString();

/** Period-over-period delta pill. Direction is conveyed by arrow + value
 *  (text), never colour alone. `value` is a signed percentage or null. */
const DeltaBadge = ({ value }) => {
  if (value == null) return <span className="au-delta au-delta--flat" title="No prior period to compare">—</span>;
  const cls = value > 0 ? 'au-delta--up' : value < 0 ? 'au-delta--down' : 'au-delta--flat';
  const arrow = value > 0 ? '▲' : value < 0 ? '▼' : '±';
  return <span className={`au-delta ${cls}`}>{arrow} {Math.abs(value)}%</span>;
};

const DEVICE_CARDS = [
  { key: 'android', label: 'Android', icon: '▲' },
  { key: 'ios', label: 'iOS', icon: '' },
  { key: 'android_watch', label: 'Android watch', icon: '⌚' },
  { key: 'ios_watch', label: 'iOS watch', icon: '⌚' },
];

const ActiveUsersSummaryCards = ({ metrics = null, summary = null, summaryDeltas = null, onViewWauUsers = null }) => (
  <>
    {metrics && (
      <div className="au-cards" data-testid="au-metrics">
        <div className="au-card au-card--accent">
          <span className="au-card__label">DAU</span>
          <span className="au-card__value tnum">{fmt(metrics.dau)}</span>
          <span className="au-card__def">Active on {metrics.anchor_date || 'the anchor day'}</span>
          {metrics.dau_avg_prev_7 != null && (
            <span className="au-card__sub" data-testid="au-dau-avg7">
              <b className="tnum">{fmt(metrics.dau_avg_prev_7)}</b> avg / day · previous 7 days (excl. {metrics.anchor_date || 'anchor'})
            </span>
          )}
        </div>
        <div className="au-card au-card--accent">
          <span className="au-card__label">WAU</span>
          <span className="au-card__value tnum">{fmt(metrics.wau)}</span>
          <span className="au-card__def">Distinct users · 7 days ending {metrics.anchor_date || 'anchor'}</span>
          {onViewWauUsers && n(metrics.wau) > 0 && (
            <button type="button" className="au-card__action" onClick={onViewWauUsers} data-testid="au-wau-view">
              View users
            </button>
          )}
        </div>
        <div className="au-card au-card--accent">
          <span className="au-card__label">MAU</span>
          <span className="au-card__value tnum">{fmt(metrics.mau)}</span>
          <span className="au-card__def">Distinct users · 30 days ending {metrics.anchor_date || 'anchor'}</span>
        </div>
        <div className="au-card au-card--accent">
          <span className="au-card__label">Stickiness</span>
          <span className="au-card__value tnum">
            {metrics.stickiness_pct == null ? '—' : `${metrics.stickiness_pct}%`}
          </span>
          <span className="au-card__def">DAU ÷ MAU</span>
        </div>
      </div>
    )}

    {summary && (
      <div className="au-cards" data-testid="au-device-cards">
        {DEVICE_CARDS.map((c) => (
          <div className="au-card" key={c.key}>
            <span className="au-card__label">{c.icon ? `${c.icon} ` : ''}{c.label}</span>
            <div className="au-card__pair">
              <span><b className="tnum">{fmt(summary[`total_${c.key}`])}</b><em>total</em></span>
              <span><b className="tnum">{fmt(summary[`avg_${c.key}`])}</b><em>avg / day</em></span>
            </div>
            {summaryDeltas && (
              <div className="au-card__delta">
                <DeltaBadge value={summaryDeltas[c.key]} />
                <span className="au-card__delta-lbl">vs prev period</span>
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </>
);

export default ActiveUsersSummaryCards;
